import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type CreateCategoryInput,
  type CreateProductInput,
  type UpdateCategoryInput,
  type UpdateProductInput,
  cafeteriaApi,
} from '../cafeteria';

const keys = {
  categories: ['cafeteria', 'categories'] as const,
  products: (categoryId?: string) => ['cafeteria', 'products', categoryId ?? 'all'] as const,
};

export function useCafeteriaCategoriesQuery() {
  return useQuery({ queryKey: keys.categories, queryFn: () => cafeteriaApi.listCategories() });
}

export function useCafeteriaProductsQuery(categoryId?: string) {
  return useQuery({ queryKey: keys.products(categoryId), queryFn: () => cafeteriaApi.listProducts(categoryId) });
}

// NOTE: prefixed with "Cafeteria" (unlike the brief's assumed names) because
// `useCreateCategoryMutation`/`useUpdateCategoryMutation`/`useDeleteCategoryMutation` already
// exist in `sand-dollar-awards/hooks/use-admin-sand-dollars.ts` and both are re-exported via the
// `services/index.ts` barrel — the unprefixed names caused a rolldown AMBIGUOUS_EXTERNAL_NAMESPACES
// build failure. Renamed all cafeteria mutation hooks for consistency with the query hooks above.
export function useCreateCafeteriaCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCategoryInput) => cafeteriaApi.createCategory(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.categories }),
  });
}

export function useUpdateCafeteriaCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCategoryInput }) => cafeteriaApi.updateCategory(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.categories }),
  });
}

export function useDeleteCafeteriaCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cafeteriaApi.deleteCategory(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.categories }),
  });
}

export function useCreateCafeteriaProductMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProductInput) => cafeteriaApi.createProduct(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cafeteria', 'products'] }),
  });
}

export function useUpdateCafeteriaProductMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateProductInput }) => cafeteriaApi.updateProduct(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cafeteria', 'products'] }),
  });
}

export function useDeleteCafeteriaProductMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cafeteriaApi.deleteProduct(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cafeteria', 'products'] }),
  });
}
